import {
  detectCurrentStep,
  TOOL_SELECT_PREFIX,
  type BuilderMessage,
} from "@/lib/promptBuilderUtils";

export type JourneyFlow = "builder" | "improved" | "remix";

export interface JourneyConversation {
  initial_goal?: string | null;
  final_prompt?: string | null;
  messages_json?: unknown;
}

export interface JourneyReceiptStep {
  num: number;
  label: string;
  body?: string | null;
  complete: boolean;
}

export interface PromptJourneyReceipt {
  flow: JourneyFlow;
  objective?: string | null;
  originalPrompt?: string | null;
  builderSteps: JourneyReceiptStep[];
  selectedTool?: string | null;
  promptCreated?: string | null;
}

export const BUILDER_RECEIPT_STEPS = [
  { num: 1, label: "AI Persona" },
  { num: 2, label: "Context & Goal" },
  { num: 3, label: "Task & Sources" },
  { num: 4, label: "Output Format" },
] as const;

export function stepNameToNumber(step: string | null): number {
  switch (step) {
    case "persona":
      return 1;
    case "context":
      return 2;
    case "task":
    case "sources":
      return 3;
    case "output_format":
      return 4;
    default:
      return 0;
  }
}

export function normalizeBuilderMessages(rawMessages: unknown): BuilderMessage[] {
  if (!Array.isArray(rawMessages)) return [];

  return rawMessages
    .filter((message): message is { role: "user" | "assistant"; content: string; id?: string; timestamp?: string } => {
      if (!message || typeof message !== "object") return false;
      const candidate = message as { role?: unknown; content?: unknown };
      return (
        (candidate.role === "user" || candidate.role === "assistant") &&
        typeof candidate.content === "string"
      );
    })
    .map((message, index) => ({
      id: message.id || `journey-${index}`,
      role: message.role,
      content: message.content,
      timestamp: message.timestamp ? new Date(message.timestamp) : new Date(),
    }));
}

export function extractBuilderStepResponses(messages: BuilderMessage[]): Record<number, string> {
  const responses: Record<number, string> = {};
  let currentStepForMsg = 0;

  for (const msg of messages) {
    if (msg.role === "assistant") {
      const detected = stepNameToNumber(detectCurrentStep(msg.content));
      if (detected > 0) currentStepForMsg = detected;
    } else if (
      msg.role === "user" &&
      currentStepForMsg > 0 &&
      !msg.content.startsWith(TOOL_SELECT_PREFIX)
    ) {
      responses[currentStepForMsg] = msg.content;
    }
  }

  return responses;
}

export function extractSelectedTool(messages: BuilderMessage[]): string | null {
  const toolMessage = [...messages]
    .reverse()
    .find((message) => message.role === "user" && message.content.startsWith(TOOL_SELECT_PREFIX));

  return toolMessage ? toolMessage.content.slice(TOOL_SELECT_PREFIX.length).trim() : null;
}

export function derivePromptJourneyReceipt({
  conversation,
  isRemix = false,
  originalPrompt,
  promptSummary,
}: {
  conversation?: JourneyConversation | null;
  isRemix?: boolean;
  originalPrompt?: string | null;
  promptSummary?: string | null;
}): PromptJourneyReceipt {
  const messages = normalizeBuilderMessages(conversation?.messages_json);
  const hasBuilderContext = Boolean(conversation && (messages.length > 0 || conversation.initial_goal || conversation.final_prompt));
  const flow: JourneyFlow = isRemix ? "remix" : hasBuilderContext ? "builder" : "improved";
  const stepResponses = extractBuilderStepResponses(messages);

  return {
    flow,
    objective: conversation?.initial_goal || promptSummary || null,
    originalPrompt: originalPrompt || null,
    builderSteps: BUILDER_RECEIPT_STEPS.map((step) => ({
      ...step,
      body: stepResponses[step.num] || null,
      complete: Boolean(stepResponses[step.num]),
    })),
    selectedTool: extractSelectedTool(messages),
    promptCreated: promptSummary || conversation?.final_prompt || null,
  };
}
